import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Text,
  VStack
} from '@chakra-ui/react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import TwygsEditorNodes from '../../../../components/Lexical/nodes/TwygsEditorNodes';
import getTheme from '../../../../components/Lexical/themes/TwygsEditorTheme';
import { SharedHistoryContext } from '../../../../components/Lexical/context/SharedHistoryContext';
import { CommentEditor } from '../../../../components/Lexical/CommentEditor';
import { ChevronDownIcon } from 'lucide-react';
import useConvertLexicalScope from '../../../../components/Lexical/hooks/useConvertLexicalScope';
import { useGeneralComments } from './hooks/useGeneralComments';
import CommentItem from '../../../../components/Lexical/components/CommentsSidebar/components/CommentItem';
import { useSearchParams } from 'react-router-dom';
import DataLayer from '../../../../utils/data-layer';
import { profileAtom } from 'src/components/Settings/atom';
import { useAtom } from 'jotai';
import { UnauthorizedDispatcher } from 'src/utils/unauthorized-dispatcher';

const GeneralComment = ({
  commentCount,
  article,
  showSensitiveContentCard,
  isShowSensitive,
  groupId
}: {
  commentCount: number;
  article: any;
  showSensitiveContentCard: boolean;
  isShowSensitive: boolean;
  groupId?: string;
}) => {
  const [editorKey, setEditorKey] = useState(Date.now());
  const [searchParams] = useSearchParams();
  const [notificationCommentId, setNotificationCommentId] = useState<string | null>(null);
  const [profile] = useAtom(profileAtom);
  const userId = JSON.parse(localStorage.getItem('user'))?.id;

  useEffect(() => {
    const commentId = searchParams.get('commentId');
    setNotificationCommentId(commentId);
  }, [searchParams]);

  const editorStateRef = useRef(null);
  const sortCommentValue = [
    { value: 'mostRecent', label: 'Most Recent' },
    { value: 'mostRelevant', label: 'Most Relevant' }
  ];
  const [sortValue, setSortValue] = useState({
    value: 'mostRecent',
    label: 'Most Recent'
  });

  const convertLexicalScope = useConvertLexicalScope();
  const {
    generalComments,
    generalCommentsLoading,
    createGeneralComment,
    updateComment,
    isReacting,
    deleteComment,
    reactToComment,
    refetchGeneralComments,
    isFetchingNextPageGeneralComments,
    shouldShowLoadMore,
    handleLoadMore
  } = useGeneralComments(article?.id, sortValue.value, 6, 10, notificationCommentId, groupId);

  const [flatComments, setFlatComments] = useState([]);
  const [isChecked, setIsChecked] = useState(true);
  const [isCreatingGeneralComment, setIsCreatingGeneralComment] = useState(false);

  const initialConfig = {
    editorState: undefined,
    namespace: 'Twygs-general-comment-editor',
    autofocus: false,
    nodes: [...TwygsEditorNodes],
    onError: (error: Error) => {
      throw error;
    },
    theme: getTheme(true)
  };

  const handleSortChange = async (newSortValue: { value: string; label: string }) => {
    setSortValue(newSortValue);
    try {
      await refetchGeneralComments();
    } catch (error) {
      console.error('Failed to fetch sorted comments:', error);
    }
  };

  const handlePost = async () => {
    setIsCreatingGeneralComment(true);
    if (editorStateRef.current) {
      const convertedLexicalScope = await convertLexicalScope({
        editorState: editorStateRef.current
      });
      try {
        await createGeneralComment({
          articleId: article?.id,
          content: convertedLexicalScope,
          isPublishProfile: isChecked,
          ...(groupId && { newsEventGroupId: groupId })
        });

        // Track article comment event
        if (article) {
          const articleType = article.writingType?.name;
          DataLayer.trackArticleComment(articleType, article.id);
        }

        refetchGeneralComments(); // Fetch recent comments
        setSortValue({ value: 'mostRecent', label: 'Most Recent' });
        setIsChecked(true);
        // Reset the editor by updating the key
        setEditorKey(Date.now()); // This forces the editor to remount
      } catch (error) {
        console.error('Failed to post comment:', error);
      } finally {
        setIsCreatingGeneralComment(false);
      }
    }
  };

  const handleReaction = async (commentId: string, reactionType: string, isLike: boolean) => {
    try {
      if (!userId) return UnauthorizedDispatcher();
      await reactToComment({
        commentId,
        nodeId: undefined,
        reactionType,
        isLike
      });

      // update reactions for both parent and reply comments
      setFlatComments((prev) => {
        return prev.map((comment) => {
          if (comment.id === commentId) {
            return {
              ...comment,
              isReacted: isLike,
              reactionNumber: isLike ? comment.reactionNumber + 1 : comment.reactionNumber - 1
            };
          }
          return comment;
        });
      });
    } catch (error) {
      console.error('Failed to react to comment:', error);
    }
  };

  useEffect(() => {
    if (generalComments?.pages?.length > 0) {
      const newFlatComments = generalComments?.pages?.flatMap((page) => page.data) || [];
      setFlatComments(newFlatComments);
    }
  }, [generalComments]);

  return (
    <Box
      id="general-comment"
      className={`${showSensitiveContentCard && !isShowSensitive && 'blur-lg'}`}
    >
      <Flex m={0} pb={0} flexDirection="column" width="100%" maxW={'unset'}>
        <HStack justify="space-between" mt={6} mb={{ base: 6, md: 8 }}>
          <Text color="growth-green" fontWeight="800" fontSize={{ base: 'md', md: 'xl' }}>
            Comments ({commentCount || 0})
          </Text>
        </HStack>
      </Flex>
      <VStack spacing={0} align="stretch" pb={10}>
        <Box>
          <LexicalComposer initialConfig={initialConfig} key={editorKey}>
            <SharedHistoryContext>
              <CommentEditor
                onPost={handlePost}
                articleId={article?.id}
                currentUser={profile}
                editorStateRef={editorStateRef}
                isLoading={isCreatingGeneralComment}
                shouldOnlyResetText
                isChecked={isChecked}
                setIsChecked={setIsChecked}
                onCancel={() => {}}
              />
            </SharedHistoryContext>
          </LexicalComposer>
        </Box>

        <Menu autoSelect={false}>
          <MenuButton
            textAlign={'left'}
            w={'142px'}
            bg={'unset'}
            h={'24px'}
            border={'none'}
            outline={'none'}
            fontWeight={800}
            _hover={{ bgColor: 'unset' }}
            _focus={{ bgColor: 'unset' }}
            _focusVisible={{ bgColor: 'unset' }}
            fontSize={'md'}
            color={'jet-black'}
            as={Button}
            rightIcon={<ChevronDownIcon />}
            boxShadow={'none'}
          >
            {sortValue.label}
          </MenuButton>
          <MenuList p={'0.625rem'} shadow={'md'}>
            {sortCommentValue.map((option) => (
              <MenuItem
                _hover={{ bgColor: 'anti-flash-white' }}
                rounded={'0.5rem'}
                py={'8px'}
                px={'10px'}
                fontWeight={600}
                _focus={{ bgColor: 'anti-flash-white' }}
                bg={option.value === sortValue.value ? 'anti-flash-white' : 'transparent'} // Highlight selected
                _focusVisible={{
                  bg: 'unset'
                }}
                key={option.value}
                onClick={() => handleSortChange(option)}
              >
                {option.label}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>

        <VStack spacing={0} align="stretch" mt={'32px'}>
          {generalCommentsLoading ? (
            <Box p={4} textAlign="center">
              <Spinner size="md" color="growth-green" />
            </Box>
          ) : (
            flatComments?.map((comment) => (
              <CommentItem
                currentUser={profile}
                key={comment.id}
                comment={comment}
                convertLexicalScope={convertLexicalScope}
                handleReaction={handleReaction}
                refetchComments={refetchGeneralComments}
                updateComment={updateComment}
                reactToComment={reactToComment}
                isCreatingComment={isCreatingGeneralComment}
                isReacting={isReacting}
                createComment={createGeneralComment}
                deleteComment={deleteComment}
                modalType="general"
                articleId={article?.id}
                article={article}
              />
            ))
          )}

          {/* Load More Button */}
          {shouldShowLoadMore && (
            <Box textAlign="center" mt={6}>
              <Button
                onClick={handleLoadMore}
                variant="outline"
                size="md"
                borderColor="gray.300"
                color="jet-black"
                bg="white"
                fontWeight={600}
                fontSize="sm"
                px={6}
                py={2}
                h="auto"
                borderRadius="8px"
                _hover={{
                  borderColor: 'growth-green',
                  color: 'growth-green',
                  bg: 'gray.50'
                }}
                _focus={{
                  borderColor: 'growth-green',
                  boxShadow: '0 0 0 1px var(--chakra-colors-growth-green)'
                }}
                _active={{
                  bg: 'gray.100'
                }}
                transition="all 0.2s ease"
              >
                Load More Comments
              </Button>
            </Box>
          )}
        </VStack>
      </VStack>
      {isFetchingNextPageGeneralComments && (
        <Flex justifyContent={'center'}>
          <Spinner size="md" color="growth-green" />
        </Flex>
      )}
    </Box>
  );
};
export default GeneralComment;
